export interface UpdateInfo {
  currentVersion: string;
  latestVersion: string;
  updateAvailable: boolean;
  updateUrl: string | null;
}

export interface UpdateCheckResult {
  updateAvailable: boolean;
  currentVersion: string;
  latestVersion: string;
  updateUrl?: string;
}

export class UpdateService {
  private serviceUrl = 'http://127.0.0.1:28473';
  private updateCheckUrl: string | null = null;

  /**
   * Checks if an update is available for the device bridge service
   */
  async checkForUpdate(currentVersion?: string): Promise<UpdateCheckResult> {
    try {
      // First, check with the service itself
      const response = await fetch(`${this.serviceUrl}/api/update/check`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000)
      });

      if (response.ok) {
        const updateInfo: UpdateInfo = await response.json();

        // Compare versions if current version is provided
        let updateAvailable = updateInfo.updateAvailable;
        if (currentVersion && updateInfo.latestVersion) {
          updateAvailable = this.compareVersions(currentVersion, updateInfo.latestVersion) < 0;
        }

        return {
          updateAvailable,
          currentVersion: updateInfo.currentVersion,
          latestVersion: updateInfo.latestVersion,
          updateUrl: updateInfo.updateUrl || undefined,
        };
      }

      // If service doesn't support update check, return no update
      return {
        updateAvailable: false,
        currentVersion: currentVersion || 'unknown',
        latestVersion: currentVersion || 'unknown'
      };
    } catch (error: any) {
      // Service not running or update check failed
      return {
        updateAvailable: false,
        currentVersion: currentVersion || 'unknown',
        latestVersion: currentVersion || 'unknown'
      };
    }
  }

  /**
   * Compares two semantic version strings
   * Returns: -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2
   */
  private compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;

      if (part1 < part2) return -1;
      if (part1 > part2) return 1;
    }

    return 0;
  }

  /**
   * Sets a custom update check URL (for remote update server)
   */
  setUpdateCheckUrl(url: string | null): void {
    this.updateCheckUrl = url;
  }
}

export const updateService = new UpdateService();

